import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Users, 
  Building2, 
  Clock, 
  Calendar, 
  CheckCircle, 
  Target, 
  FileText,
  Plus,
  ChevronRight,
  Info,
  TrendingUp,
  Activity
} from 'lucide-react';

export const DashboardHelpPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      id: 'header',
      title: 'Header e Benvenuto',
      description: 'La sezione superiore della dashboard ti dà il benvenuto e offre accesso rapido alle funzioni principali.',
      details: [
        'Saluto personalizzato con il tuo nome',
        'Pulsante "Nuova Candidatura" per aggiungere velocemente una nuova candidatura',
        'Design responsive che si adatta a tutti i dispositivi'
      ],
      icon: <Users className="h-5 w-5" />
    },
    {
      id: 'stats',
      title: 'Statistiche Principali',
      description: 'Quattro card che mostrano un riepilogo immediato delle tue candidature.',
      details: [
        '**Totale Candidature**: Numero complessivo di candidature inviate',
        '**In Attesa**: Candidature ancora in fase di valutazione',
        '**Colloqui**: Candidature con colloquio programmato',
        '**Accettate**: Candidature che hanno avuto esito positivo'
      ],
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      id: 'chart',
      title: 'Grafico Distribuzione Stati',
      description: 'Un grafico a torta che mostra visivamente la distribuzione delle tue candidature per stato.',
      details: [
        'Visualizzazione immediata delle percentuali',
        'Colori distintivi per ogni stato',
        'Legenda con conteggi dettagliati',
        'Interattivo con tooltip al passaggio del mouse'
      ],
      icon: <Target className="h-5 w-5" />
    },
    {
      id: 'activity',
      title: 'Attività Recente',
      description: 'Timeline delle tue ultime attività e candidature più recenti.',
      details: [
        'Lista delle candidature più recenti',
        'Stato attuale di ogni candidatura',
        'Data di invio per ogni candidatura',
        'Link diretto ai dettagli della candidatura'
      ],
      icon: <Activity className="h-5 w-5" />
    },
    {
      id: 'quick-actions',
      title: 'Azioni Rapide',
      description: 'Pulsanti per accedere velocemente alle funzioni più utilizzate.',
      details: [
        '**Gestisci Candidature**: Vai alla lista completa delle candidature',
        '**Calendario**: Accedi al calendario dei colloqui'
      ],
      icon: <Plus className="h-5 w-5" />
    }
  ];

  const tips = [
    'Clicca su qualsiasi card statistica per filtrare le candidature per quello stato',
    'Usa il pulsante "Vedi tutte" nell\'attività recente per accedere alla lista completa',
    'Il grafico si aggiorna automaticamente quando aggiungi nuove candidature',
    'Su mobile, le statistiche sono organizzate in una griglia 2x2 per una migliore visualizzazione'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/help')}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
                <Users className="h-6 w-6 text-blue-700" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Guida Dashboard Utente
                </h1>
                <p className="text-sm text-gray-600">
                  Scopri come utilizzare al meglio la tua dashboard personale
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Panoramica */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Info className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Panoramica
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                La Dashboard Utente è il tuo centro di controllo principale. Qui puoi vedere a colpo d'occhio 
                tutte le tue candidature, monitorare i progressi e accedere rapidamente alle funzioni più importanti. 
                La dashboard si aggiorna automaticamente e mostra sempre i dati più recenti.
              </p>
            </div>
          </div>
        </div>

        {/* Funzionalità */}
        <div className="space-y-4 lg:space-y-6">
          {features.map((feature, index) => (
            <div key={feature.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: detail }} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Suggerimenti */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <Info className="h-5 w-5" />
            Suggerimenti Utili
          </h3>
          <ul className="space-y-2">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-blue-800">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Azioni */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Pronto a iniziare?
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Users className="h-4 w-4" />
                Vai alla Dashboard
              </button>
              <button
                onClick={() => navigate('/applications/new')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Nuova Candidatura
              </button>
              <button
                onClick={() => navigate('/help')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <FileText className="h-4 w-4" />
                Altre Guide
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 